import { useEffect, useState, type FormEvent } from "react";

import {
  ApiError,
  customerLogin,
  customerLogout,
  getCustomerSession,
  getPersonalLawyerInvitation,
} from "../api/client";
import type { LawyerInvitation } from "../customer-personal/InviteLawyer";
import "./invitation.css";

function loginErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return "服务暂时不可用，请稍后重试";
  if (error.code === "INVALID_CREDENTIALS") return "登录账号或密码错误";
  if (error.code === "ACCOUNT_DISABLED") return "该账号已停用，请联系平台管理员";
  return error.message;
}

function isMissingCustomerSession(error: unknown): boolean {
  return error instanceof ApiError
    && error.status === 401
    && (error.code === "CUSTOMER_LOGIN_REQUIRED"
      || error.code === "CUSTOMER_SESSION_INVALID");
}

export function LawyerInvitationH5App() {
  const [checkingSession, setCheckingSession] = useState(true);
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [invitation, setInvitation] = useState<LawyerInvitation | null>(null);

  useEffect(() => {
    let active = true;
    void getCustomerSession().then(async (identity) => {
      if (!active) return;
      if (!identity.is_personal_lawyer) {
        setError("当前登录的不是律师个人账号，请切换账号。");
        setShowLogin(true);
        return;
      }
      try {
        setInvitation(await getPersonalLawyerInvitation());
      } catch (reason) {
        if (active) setError(loginErrorMessage(reason));
      }
    }).catch((reason) => {
      if (!active) return;
      if (isMissingCustomerSession(reason)) {
        setShowLogin(true);
      } else {
        setError(loginErrorMessage(reason));
      }
    }).finally(() => {
      if (active) setCheckingSession(false);
    });
    return () => { active = false; };
  }, []);

  async function login(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!/^1\d{10}$/.test(account.trim())) {
      setError("请输入正确的手机号");
      return;
    }
    if (!password) {
      setError("请输入登录密码");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const identity = await customerLogin(account.trim(), password);
      if (!identity.is_personal_lawyer) {
        await customerLogout();
        setError("仅律师个人账号可生成邀请链接");
        return;
      }
      setShowLogin(false);
      setInvitation(await getPersonalLawyerInvitation());
    } catch (reason) {
      setError(loginErrorMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  async function copyInvitation() {
    if (!invitation) return;
    try {
      await navigator.clipboard.writeText(invitation.url);
      setNotice("邀请链接已复制");
    } catch {
      setError("复制失败，请长按邀请链接复制");
    }
  }

  async function shareInvitation() {
    if (!invitation) return;
    if (!navigator.share) {
      await copyInvitation();
      return;
    }
    try {
      await navigator.share({
        title: "邀请律师加入中承信合平台",
        text: "请通过我的专属链接注册律师个人账号",
        url: invitation.url,
      });
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === "AbortError") return;
      setError("分享失败，请复制邀请链接");
    }
  }

  async function switchAccount() {
    setBusy(true);
    setError("");
    try {
      await customerLogout();
      setInvitation(null);
      setShowLogin(true);
      setPassword("");
      setNotice("");
    } catch {
      setError("切换账号失败，请刷新页面重试");
    } finally {
      setBusy(false);
    }
  }

  return <main className="lawyer-invitation-h5-page">
    <div className="lawyer-invitation-h5-brand">中承信合数据服务平台</div>
    {checkingSession ? <section className="lawyer-invitation-h5-card is-loading">正在加载…</section> : invitation ? <section className="lawyer-invitation-h5-card">
      <header><span>律师邀请</span><h1>我的专属邀请</h1><p>将这个链接发给其他律师，对方完成注册后将与您建立邀请关系。</p></header>
      <div className="lawyer-invitation-h5-code"><small>我的专属邀请码</small><strong>{invitation.code}</strong></div>
      <label className="lawyer-invitation-h5-link"><span>专属邀请链接</span><input value={invitation.url} readOnly /></label>
      <div className="lawyer-invitation-h5-actions"><button type="button" onClick={() => void copyInvitation()}>复制邀请链接</button><button className="is-primary" type="button" onClick={() => void shareInvitation()}>分享邀请链接</button></div>
      {notice && <p className="lawyer-invitation-h5-notice" role="status">{notice}</p>}
      <p className="lawyer-invitation-h5-rule">邀请关系以对方通过此链接完成注册为准；已绑定的账号不会重复绑定或改绑。</p>
      <button className="lawyer-invitation-h5-switch" type="button" disabled={busy} onClick={() => void switchAccount()}>切换账号</button>
    </section> : showLogin ? <form className="lawyer-invitation-h5-card" onSubmit={(event) => void login(event)}>
      <header><span>律师邀请</span><h1>生成我的邀请链接</h1><p>使用律师个人账号登录，无需进入 PC 工作台。</p></header>
      <label><span>登录账号</span><input autoComplete="username" inputMode="numeric" value={account} placeholder="请输入注册手机号" onChange={(event) => setAccount(event.target.value)} /></label>
      <label><span>登录密码</span><input autoComplete="current-password" type="password" value={password} placeholder="请输入平台登录密码" onChange={(event) => setPassword(event.target.value)} /></label>
      <button className="lawyer-invitation-h5-submit" type="submit" disabled={busy}>{busy ? "正在生成…" : "登录并生成邀请链接"}</button>
    </form> : <section className="lawyer-invitation-h5-card"><header><span>律师邀请</span><h1>邀请链接加载失败</h1><p>请稍后刷新页面重试。</p></header></section>}
    {error && <div className="lawyer-invitation-h5-error" role="alert">{error}</div>}
  </main>;
}
